/**
 * Clinic Representative Dashboard
 * Main dashboard for clinic representatives to manage patient operations
 */
import React, { useState, useEffect } from 'react';
import { formatDateLocale, formatDateTimeLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClinicRepAuth } from '../../context/ClinicRepresentativeAuthContext';
import { useClinicRepData, OPERATIONAL_STATUSES } from '../../context/ClinicRepresentativeDataContext';
import { useMessaging } from '../../context/MessagingContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Building2, LogOut, Users, Plane, Calendar, MessageSquare, FileText,
  Search, Clock, CheckCircle, MapPin, Phone, Mail, Hotel, Car,
  Stethoscope, Heart, ChevronRight, X, StickyNote, Plus, ExternalLink,
  ChevronLeft
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';

// Format date helper
const formatDate = (dateStr, na) => {
  if (!dateStr) return na || 'N/A';
  return formatDateLocale(dateStr);
};

// Patient Card Component
const PatientCard = ({ patient, onSelect, isSelected }) => {
  const { t } = useTranslation();
  const status = OPERATIONAL_STATUSES[patient.operationalStatus];
  
  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-lg ${
        isSelected ? 'ring-2 ring-indigo-500 bg-indigo-50/30' : ''
      }`}
      onClick={() => onSelect(patient)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <img
            src={patient.avatar}
            alt={patient.name}
            className="w-12 h-12 rounded-full object-cover"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-800 truncate">{patient.name}</h3>
            </div>
            <p className="text-sm text-slate-600">{patient.treatment}</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={`text-xs ${status?.color || 'bg-slate-100 text-slate-600'}`}>
                {status?.label || patient.operationalStatus}
              </Badge>
              <span className="text-xs text-slate-400">
                <MapPin className="h-3 w-3 inline mr-1" />
                {patient.country}
              </span>
            </div>
          </div>
          <div className="text-right text-xs">
            <p className="text-slate-500">{t('provider.repDashboard.arrivalLabel')}</p>
            <p className="font-medium text-slate-700">{formatDate(patient.travelDetails?.arrivalDate, t('provider.repDashboard.dateNA'))}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Patient Detail Panel
const PatientDetailPanel = ({ patient, representative, onClose, onUpdateStatus, onAddNote, onOpenMessaging }) => {
  const { t } = useTranslation();
  const [newNote, setNewNote] = useState('');
  const status = OPERATIONAL_STATUSES[patient.operationalStatus];
  
  const handleAddNote = () => {
    if (!newNote.trim()) return;
    onAddNote(patient.id, newNote, representative.name);
    setNewNote('');
    toast({
      title: t('provider.repDashboard.toastNoteAddedTitle'),
      description: t('provider.repDashboard.toastNoteAddedBody')
    });
  };

  const handleStatusUpdate = (newStatus) => {
    onUpdateStatus(patient.id, newStatus, representative.name);
    toast({
      title: t('provider.repDashboard.toastStatusUpdatedTitle'),
      description: t('provider.repDashboard.toastStatusUpdatedBody', { label: OPERATIONAL_STATUSES[newStatus].label })
    });
  };

  // P3-3 J1: Handle messaging - open conversation or create new thread
  const handleOpenMessaging = () => {
    onOpenMessaging(patient);
  };

  // Determine next available status actions
  const getNextStatusActions = () => {
    const statusFlow = {
      'ARRIVAL_PENDING': ['ARRIVED'],
      'ARRIVED': ['PICKED_UP'],
      'PICKED_UP': ['CHECKED_IN'],
      'CHECKED_IN': ['IN_TREATMENT'],
      'IN_TREATMENT': ['POST_OP'],
      'POST_OP': ['COMPLETED'],
      'COMPLETED': []
    };
    return statusFlow[patient.operationalStatus] || [];
  };

  // Get all available statuses for dropdown (Item 4.1 - explicit status selection)
  const getAllStatuses = () => {
    return Object.keys(OPERATIONAL_STATUSES);
  };

  const [selectedStatus, setSelectedStatus] = useState(patient.operationalStatus);
  const [isUpdating, setIsUpdating] = useState(false);

  // Priority 1 Item 5: Handle explicit status update with confirmation
  const handleExplicitStatusUpdate = async () => {
    if (selectedStatus === patient.operationalStatus) return;
    
    setIsUpdating(true);
    await handleStatusUpdate(selectedStatus);
    setIsUpdating(false);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b bg-white">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <img
              src={patient.avatar}
              alt={patient.name}
              className="w-14 h-14 rounded-full object-cover"
            />
            <div>
              <h2 className="text-lg font-bold text-slate-800">{patient.name}</h2>
              <p className="text-sm text-slate-600">{patient.treatment}</p>
              <Badge className={`mt-1 ${status?.color}`}>
                {status?.label}
              </Badge>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} title={t('provider.repDashboard.closePanel')}>
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Quick Actions - Status Updates (Priority 1 Item 5 - Explicit Update Button) */}
        <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
          <CardContent className="p-4">
            <p className="text-sm font-medium text-indigo-800 mb-2">{t('provider.repDashboard.updateStatus')}</p>
            
            {/* Quick Action - Next Status */}
            {getNextStatusActions().length > 0 && (
              <div className="flex gap-2 flex-wrap mb-3">
                {getNextStatusActions().map(nextStatus => (
                  <Button
                    key={nextStatus}
                    size="sm"
                    onClick={() => handleStatusUpdate(nextStatus)}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    {t('provider.repDashboard.markAs', { label: OPERATIONAL_STATUSES[nextStatus].label })}
                  </Button>
                ))}
              </div>
            )}

            {/* Explicit Status Dropdown with Update Button */}
            <div className="border-t pt-3 mt-2">
              <p className="text-xs text-slate-500 mb-2">{t('provider.repDashboard.orSelectAny')}</p>
              <div className="flex gap-2">
                <select
                  className="flex-1 border rounded-lg p-2 text-sm bg-white"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  {getAllStatuses().map(statusKey => (
                    <option key={statusKey} value={statusKey}>
                      {OPERATIONAL_STATUSES[statusKey].label}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={handleExplicitStatusUpdate}
                  disabled={selectedStatus === patient.operationalStatus || isUpdating}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isUpdating ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-1" />
                      {t('provider.repDashboard.updateStatusBtn')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Phone className="h-4 w-4 text-slate-500" />
              {t('provider.repDashboard.contactInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-slate-400" />
              <span>{patient.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-slate-400" />
              <span>{patient.phone}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-slate-400" />
              <span>{patient.city}, {patient.country}</span>
            </div>
            {patient.emergencyContact && (
              <div className="pt-2 border-t mt-2">
                <p className="text-xs text-slate-500 mb-1">{t('provider.repDashboard.emergencyContact')}</p>
                <p>{patient.emergencyContact.name} ({patient.emergencyContact.relationship})</p>
                <p className="text-slate-600">{patient.emergencyContact.phone}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Travel Details */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Plane className="h-4 w-4 text-slate-500" />
              {t('provider.repDashboard.travelDetails')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-slate-500">{t('provider.repDashboard.arrival')}</p>
                <p className="font-medium">{formatDate(patient.travelDetails?.arrivalDate, t('provider.repDashboard.dateNA'))}</p>
                <p className="text-slate-600">{patient.travelDetails?.arrivalTime}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">{t('provider.repDashboard.departure')}</p>
                <p className="font-medium">{formatDate(patient.travelDetails?.departureDate, t('provider.repDashboard.dateNA'))}</p>
                <p className="text-slate-600">{patient.travelDetails?.departureTime}</p>
              </div>
            </div>
            <div className="pt-2 border-t">
              <p className="text-xs text-slate-500">{t('provider.repDashboard.flight')}</p>
              <p>{patient.travelDetails?.airline} - {patient.travelDetails?.flightNumber}</p>
              <p className="text-slate-600">{patient.travelDetails?.arrivalAirport}</p>
            </div>
            {patient.travelDetails?.needsPickup && (
              <div className="flex items-center gap-2 p-2 bg-indigo-50 rounded-lg">
                <Car className="h-4 w-4 text-indigo-600" />
                <span className="text-indigo-700 text-xs">{t('provider.repDashboard.pickupRequired')}</span>
              </div>
            )}
            {patient.travelDetails?.pickupNotes && (
              <p className="text-xs text-slate-600 italic">
                {t('provider.repDashboard.pickupNote', { text: patient.travelDetails.pickupNotes })}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Hotel Details */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Hotel className="h-4 w-4 text-slate-500" />
              {t('provider.repDashboard.accommodation')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="font-medium">{patient.hotelDetails?.name}</p>
            <p className="text-slate-600 text-xs">{patient.hotelDetails?.address}</p>
            <div className="grid grid-cols-2 gap-2 pt-2">
              <div>
                <p className="text-xs text-slate-500">{t('provider.repDashboard.checkIn')}</p>
                <p>{formatDate(patient.hotelDetails?.checkIn, t('provider.repDashboard.dateNA'))}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">{t('provider.repDashboard.checkOut')}</p>
                <p>{formatDate(patient.hotelDetails?.checkOut, t('provider.repDashboard.dateNA'))}</p>
              </div>
            </div>
            <p className="text-xs text-slate-500">{t('provider.repDashboard.room', { room: patient.hotelDetails?.roomType })}</p>
            <p className="text-xs text-slate-500">{t('provider.repDashboard.confirmation', { code: patient.hotelDetails?.confirmationNumber })}</p>
          </CardContent>
        </Card>

        {/* Surgery Details */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Stethoscope className="h-4 w-4 text-slate-500" />
              {t('provider.repDashboard.treatmentSchedule')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="font-medium text-purple-800">{patient.surgeryDetails?.procedure}</p>
              <p className="text-purple-700">{formatDate(patient.surgeryDetails?.date, t('provider.repDashboard.dateNA'))} at {patient.surgeryDetails?.time}</p>
              <p className="text-xs text-purple-600 mt-1">Dr. {patient.surgeryDetails?.doctor?.replace('Dr. ', '')}</p>
            </div>
            {patient.surgeryDetails?.preOpDate && (
              <div className="text-xs">
                <p className="text-slate-500">{t('provider.repDashboard.preOpConsult')}</p>
                <p>{formatDate(patient.surgeryDetails.preOpDate, t('provider.repDashboard.dateNA'))} at {patient.surgeryDetails.preOpTime}</p>
              </div>
            )}
            {patient.followUpVisits?.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-xs text-slate-500 mb-2">{t('provider.repDashboard.followUpVisits')}</p>
                {patient.followUpVisits.map((visit, i) => (
                  <div key={i} className="flex items-center justify-between py-1">
                    <span>{visit.type}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-600">{formatDate(visit.date, t('provider.repDashboard.dateNA'))}</span>
                      {visit.completed && (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Clinic Notes */}
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-800">
              <StickyNote className="h-4 w-4" />
              {t('provider.repDashboard.clinicNotes')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Add Note */}
            <div className="flex gap-2">
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder={t('provider.repDashboard.addNotePh')}
                className="text-sm min-h-[60px]"
              />
              <Button 
                size="sm" 
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Existing Notes */}
            {patient.clinicNotes?.length > 0 ? (
              <div className="space-y-2">
                {patient.clinicNotes.map((note) => (
                  <div key={note.id} className="p-3 bg-white rounded-lg border text-sm">
                    <p className="text-slate-700">{note.text}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                      <span>{note.createdBy}</span>
                      <span>•</span>
                      <span>{formatDateLocale(note.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-2">
                {t('provider.repDashboard.noNotesYet')}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Status History */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-500" />
              {t('provider.repDashboard.statusHistory')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {patient.statusHistory?.slice().reverse().map((entry, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
                  <Badge className={`text-xs ${OPERATIONAL_STATUSES[entry.status]?.color}`}>
                    {OPERATIONAL_STATUSES[entry.status]?.label}
                  </Badge>
                  <span className="text-xs text-slate-500">
                    {formatDateTimeLocale(entry.timestamp)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer Actions */}
      <div className="p-4 border-t bg-white space-y-2">
        <Button 
          onClick={handleOpenMessaging}
          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600"
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          {t('provider.repDashboard.messagePatient')}
        </Button>
        <Button variant="outline" className="w-full" onClick={onClose}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          {t('provider.repDashboard.backToList')}
        </Button>
      </div>
    </div>
  );
};

// P3-3 J1: Messaging Modal for Patient Communication
const MessagingModal = ({ patient, representative, isOpen, onClose }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Load messages on open - using callback pattern to avoid lint warning
  useEffect(() => {
    if (!isOpen || !patient) return;
    
    const loadConversation = () => {
      const savedConvs = sessionStorage.getItem('repPatientConversations');
      const conversations = savedConvs ? JSON.parse(savedConvs) : {};
      
      const convKey = `${representative.id}-${patient.id}`;
      if (conversations[convKey]) {
        return conversations[convKey].messages || [];
      } else {
        // Create new conversation
        const initialMessage = {
          id: `msg-${Date.now()}`,
          from: 'system',
          body: t('provider.repDashboard.conversationStarted', { name: patient.name }),
          timestamp: new Date().toISOString()
        };
        conversations[convKey] = {
          patientId: patient.id,
          patientName: patient.name,
          representativeId: representative.id,
          messages: [initialMessage],
          createdAt: new Date().toISOString()
        };
        sessionStorage.setItem('repPatientConversations', JSON.stringify(conversations));
        return [initialMessage];
      }
    };
    
    // Use timeout to ensure proper state update
    const loadedMessages = loadConversation();
    setMessages(loadedMessages);
    setIsLoading(false);
  }, [isOpen, patient, representative, t]);

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;

    const message = {
      id: `msg-${Date.now()}`,
      from: 'rep',
      senderName: representative.name,
      body: newMessage.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, message]);
    
    // Save to storage
    const savedConvs = sessionStorage.getItem('repPatientConversations');
    let conversations = savedConvs ? JSON.parse(savedConvs) : {};
    const convKey = `${representative.id}-${patient.id}`;
    if (!conversations[convKey]) {
      conversations[convKey] = { messages: [] };
    }
    conversations[convKey].messages = [...(conversations[convKey].messages || []), message];
    conversations[convKey].lastMessageAt = message.timestamp;
    sessionStorage.setItem('repPatientConversations', JSON.stringify(conversations));

    setNewMessage('');
    toast({
      title: t('provider.repDashboard.toastMessageSentTitle'),
      description: t('provider.repDashboard.toastMessageSentBody')
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-xl">
          <div className="flex items-center gap-3">
            <img src={patient.avatar} alt={patient.name} className="w-10 h-10 rounded-full" />
            <div>
              <h3 className="font-semibold">{patient.name}</h3>
              <p className="text-xs text-indigo-200">{patient.treatment}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/20">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px]">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>{t('provider.repDashboard.noMessagesYet')}</p>
              <p className="text-sm">{t('provider.repDashboard.startConversation')}</p>
            </div>
          ) : (
            messages.map(msg => (
              <div 
                key={msg.id} 
                className={`flex ${msg.from === 'rep' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] rounded-lg p-3 ${
                  msg.from === 'rep' 
                    ? 'bg-indigo-600 text-white' 
                    : msg.from === 'system'
                      ? 'bg-gray-100 text-gray-500 text-xs text-center w-full'
                      : 'bg-gray-200 text-gray-800'
                }`}>
                  <p className="text-sm">{msg.body}</p>
                  <p className={`text-xs mt-1 ${msg.from === 'rep' ? 'text-indigo-200' : 'text-gray-500'}`}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Input */}
        <div className="p-4 border-t">
          <div className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={t('provider.repDashboard.typeMessagePh')}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              className="flex-1"
            />
            <Button onClick={handleSendMessage} disabled={!newMessage.trim()}>
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
const ClinicRepresentativeDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { representative, isAuthenticated, logoutRepresentative } = useClinicRepAuth();
  const {
    getPatientsForRepresentative,
    getTodaysArrivals,
    getUpcomingPatients,
    getActivePatients,
    updateOperationalStatus,
    addClinicNote,
    getRepresentativeStats
  } = useClinicRepData();

  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [messagingPatient, setMessagingPatient] = useState(null); // P3-3 J1: Messaging state

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/clinic/representative/login');
    }
  }, [isAuthenticated, navigate]);

  if (!representative) return null;

  const allPatients = getPatientsForRepresentative(representative.id);
  const todaysArrivals = getTodaysArrivals(representative.id);
  const upcomingPatients = getUpcomingPatients(representative.id);
  const activePatients = getActivePatients(representative.id);
  const stats = getRepresentativeStats(representative.id);

  // P3-3 J1: Handle opening messaging modal
  const handleOpenMessaging = (patient) => {
    setMessagingPatient(patient);
  };

  // Filter patients based on tab and search
  const getFilteredPatients = () => {
    let patients = allPatients;
    
    switch (activeTab) {
      case 'today':
        patients = todaysArrivals;
        break;
      case 'upcoming':
        patients = upcomingPatients;
        break;
      case 'active':
        patients = activePatients;
        break;
      default:
        patients = allPatients;
    }
    
    if (searchQuery) {
      patients = patients.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.treatment.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.country.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return patients;
  };

  const filteredPatients = getFilteredPatients();

  const handleLogout = () => {
    logoutRepresentative();
    navigate('/clinic/representative/login');
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Toaster />
      
      {/* P3-3 J1: Messaging Modal */}
      {messagingPatient && (
        <MessagingModal
          patient={messagingPatient}
          representative={representative}
          isOpen={!!messagingPatient}
          onClose={() => setMessagingPatient(null)}
        />
      )}

      {/* P3-1: Header focused on "Which patients should I coordinate?" */}
      <header className="bg-gradient-to-r from-indigo-800 to-purple-900 text-white sticky top-12 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Building2 className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold" data-testid="clinic-rep-dashboard-title">{t('provider.repDashboard.pageTitle')}</h1>
                <p className="text-sm text-indigo-200">{representative.clinicName}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <img 
                  src={representative.avatar} 
                  alt={representative.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="hidden sm:block">
                  <p className="text-sm font-medium">{representative.name}</p>
                  <p className="text-xs text-indigo-200">{representative.title}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-indigo-200 hover:text-white hover:bg-white/10"
                data-testid="clinic-rep-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t('provider.repDashboard.logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* P3-1: Primary Action Banner - Today's Arrivals */}
      {todaysArrivals.length > 0 && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Plane className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800" data-testid="todays-arrivals-count">
                    {t('provider.repDashboard.arrivingToday', { count: todaysArrivals.length })}
                  </p>
                  <p className="text-sm text-amber-600">{t('provider.repDashboard.requiresCoordination')}</p>
                </div>
              </div>
              <Button 
                size="sm" 
                className="bg-amber-600 hover:bg-amber-700"
                onClick={() => setActiveTab('today')}
                data-testid="view-today-arrivals-btn"
              >
                {t('provider.repDashboard.viewArrivals')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Simplified Stats Bar */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center cursor-pointer hover:bg-slate-50 rounded p-2" onClick={() => setActiveTab('all')} data-testid="stat-total-patients">
              <p className="text-xl font-bold text-slate-800">{stats.total}</p>
              <p className="text-xs text-slate-500">{t('provider.repDashboard.statTotal')}</p>
            </div>
            <div className="text-center cursor-pointer hover:bg-slate-50 rounded p-2" onClick={() => setActiveTab('today')} data-testid="stat-arrivals">
              <p className="text-xl font-bold text-amber-600">{stats.arrivalPending}</p>
              <p className="text-xs text-slate-500">{t('provider.repDashboard.statPendingArrival')}</p>
            </div>
            <div className="text-center cursor-pointer hover:bg-slate-50 rounded p-2" onClick={() => setActiveTab('active')} data-testid="stat-active">
              <p className="text-xl font-bold text-blue-600">{stats.inTreatment}</p>
              <p className="text-xs text-slate-500">{t('provider.repDashboard.statInTreatment')}</p>
            </div>
            <div className="text-center cursor-pointer hover:bg-slate-50 rounded p-2" data-testid="stat-completed">
              <p className="text-xl font-bold text-green-600">{stats.completed}</p>
              <p className="text-xs text-slate-500">{t('provider.repDashboard.statCompleted')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6 h-[calc(100vh-340px)]">
          {/* Left Panel - Patient List */}
          <div className="w-96 flex-shrink-0 flex flex-col bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full rounded-none border-b grid grid-cols-4">
                <TabsTrigger value="all" className="text-xs" data-testid="tab-all">
                  <Users className="h-3 w-3 mr-1" />
                  {t('provider.repDashboard.tabAll')}
                </TabsTrigger>
                <TabsTrigger value="today" className="text-xs relative" data-testid="tab-today">
                  <Plane className="h-3 w-3 mr-1" />
                  {t('provider.repDashboard.tabToday')}
                  {todaysArrivals.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {todaysArrivals.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="upcoming" className="text-xs">
                  <Calendar className="h-3 w-3 mr-1" />
                  {t('provider.repDashboard.tabUpcoming')}
                </TabsTrigger>
                <TabsTrigger value="active" className="text-xs">
                  <Heart className="h-3 w-3 mr-1" />
                  {t('provider.repDashboard.tabActive')}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Search */}
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('provider.repDashboard.searchPh')}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Patient List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {filteredPatients.length > 0 ? (
                filteredPatients.map(patient => (
                  <PatientCard
                    key={patient.id}
                    patient={patient}
                    isSelected={selectedPatient?.id === patient.id}
                    onSelect={setSelectedPatient}
                  />
                ))
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">{t('provider.repDashboard.noPatientsFound')}</p>
                </div>
              )}
            </div>

            <div className="p-3 border-t bg-slate-50 text-center text-xs text-slate-500">
              {t('provider.repDashboard.showingOf', { shown: filteredPatients.length, total: allPatients.length })}
            </div>
          </div>

          {/* Right Panel - Patient Detail */}
          <div className="flex-1 bg-white rounded-xl shadow-sm overflow-hidden">
            {selectedPatient ? (
              <PatientDetailPanel
                patient={selectedPatient}
                representative={representative}
                onClose={() => setSelectedPatient(null)}
                onUpdateStatus={updateOperationalStatus}
                onAddNote={addClinicNote}
                onOpenMessaging={handleOpenMessaging}
              />
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-600">{t('provider.repDashboard.selectPatient')}</h3>
                  <p className="text-slate-500 mt-1">{t('provider.repDashboard.selectPatientHelp')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      
      {/* P3: Build stamp for deployment verification */}
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-indigo-600 text-white text-center py-1.5 font-bold text-sm shadow-lg">
      </div>
    </div>
  );
};

export default ClinicRepresentativeDashboard;
